import { Component, ViewChild, Injectable, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ReCaptcha2Component } from 'ngx-captcha';
import { EnvironmentService } from 'src/app/services/environment.service';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-support-form',
  templateUrl: './support-form.component.html',
  styleUrls: ['./support-form.component.scss']
})

export class SupportFormComponent implements OnInit {
  @ViewChild(ReCaptcha2Component) captchaComponent?: ReCaptcha2Component;
  supportForm : FormGroup;
  selectedFile?: File;
  submitting: boolean = false;
  success: boolean = false;
  error: string = '';
  siteKey: string = '';
  captchaIsLoaded = false;
  captchaSuccess = false;
  captchaIsExpired = false;
  captchaResponse?: string;
  /** True in member-app WebView / support-raw — no reCAPTCHA (unreliable in embeds). */
  isEmbedded: boolean = false;
  /** Prefill locks — use readonly, never disable (disabled => valid=false / silent Submit fail). */
  nameLocked: boolean = false;
  emailLocked: boolean = false;

  constructor(private http: HttpClient, private route: ActivatedRoute, private _environmentService: EnvironmentService) {
    this.supportForm = new FormGroup({
      name: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')]),
      message: new FormControl('', Validators.required),
      captchaResponseControl: new FormControl('', Validators.required),
    });

    this.siteKey = this._environmentService.getFacilityConfig().recaptcha.siteKey
  }

  ngOnInit() {
    // Apply immediately from snapshot so captcha never flashes in embeds.
    this.applyEmbeddedMode(this.route.snapshot.queryParams);
    this.route.queryParams.subscribe(params => this.applyEmbeddedMode(params));
  }

  private applyEmbeddedMode(params: { [key: string]: any }): void {
    // Prefer Angular params; fall back to window search (WebView / support-raw).
    const fromWindow = this.queryParamsFromWindow();
    const name = this.paramValue(params['name']) || fromWindow['name'];
    const email = this.paramValue(params['email']) || fromWindow['email'];
    const embeddedRaw =
      this.paramValue(params['embedded']) || fromWindow['embedded'];
    const embeddedFlag = embeddedRaw === '1' || embeddedRaw === 'true';
    const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
    const isSupportRaw =
      this.route.snapshot.routeConfig?.path === 'support-raw' ||
      path.includes('support-raw');
    const isMemberAppWebView =
      typeof navigator !== 'undefined' && /DropMemberApp/i.test(navigator.userAgent);

    if (name) {
      this.supportForm.controls['name'].setValue(name);
      this.nameLocked = true;
    }

    if (email) {
      this.supportForm.controls['email'].setValue(email);
      this.emailLocked = this.isValidEmail(email);
    }

    // Never show reCAPTCHA in the member-app WebView / support-raw embeds.
    this.isEmbedded =
      isSupportRaw || embeddedFlag || isMemberAppWebView || !!(name || email);

    if (this.isEmbedded) {
      this.supportForm.controls['captchaResponseControl'].clearValidators();
      this.supportForm.controls['captchaResponseControl'].updateValueAndValidity();
    }
  }

  private paramValue(value: unknown): string {
    if (Array.isArray(value)) {
      return (value[0] ?? '').toString().trim();
    }
    return (value ?? '').toString().trim();
  }

  private queryParamsFromWindow(): { [key: string]: string } {
    if (typeof window === 'undefined') {
      return {};
    }
    const out: { [key: string]: string } = {};
    const search = new URLSearchParams(window.location.search);
    search.forEach((v, k) => {
      out[k] = (v ?? '').trim();
    });
    return out;
  }

  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    this.success = false;
    this.error = '';

    if (!this.canSubmit()) {
      Object.values(this.supportForm.controls).forEach(control => control.markAsTouched());
      this.error = this.isEmbedded
        ? 'Please complete all required fields.'
        : 'Please complete all required fields and captcha.';
      return;
    }

    this.submitting = true;

    const { name, email, message } = this.supportForm.getRawValue();

    const formData = new FormData();
    formData.append('name', name ?? '');
    formData.append('email', email ?? '');
    formData.append('message', message ?? '');
    if (this.selectedFile) {
      formData.append('Attachment', this.selectedFile, this.selectedFile.name);
    }

    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');

    this.http
      .post(
        `${this._environmentService.getFacilityConfig().facilityUrl}api/v1/Facility/SupportRequest`,
        formData,
        { headers }
      )
      .pipe(finalize(() => {
        this.submitting = false;
      }))
      .subscribe({
        next: () => {
          this.success = true;
          if (!this.isEmbedded) {
            this.supportForm.reset();
            this.clearAttachmentInput();
            this.resetCaptcha();
          } else {
            this.supportForm.controls['message'].reset();
            this.clearAttachmentInput();
          }
        },
        error: (err) => {
          console.error('Error submitting form data:', err);
          this.error = err?.error?.message || 'An error occurred while submitting the form.';
          if (!this.isEmbedded) {
            this.resetCaptcha();
          }
        }
      });
  }

  handleSuccess(captchaResponse: string | Event) {
    const token = typeof captchaResponse === 'string'
      ? captchaResponse
      : (captchaResponse.target as HTMLInputElement | undefined)?.value ?? '';
    this.captchaResponse = token;
    this.supportForm.controls['captchaResponseControl'].setValue(token);
    this.captchaSuccess = true;
    this.captchaIsExpired = false;
  }

  handleLoad(): void {
    this.captchaIsLoaded = true;
  }

  handleExpire(): void {
    this.captchaIsExpired = true;
    this.captchaSuccess = false;
    this.supportForm.controls['captchaResponseControl'].reset();
  }

  resetCaptcha(): void {
    this.captchaSuccess = false;
    this.captchaIsExpired = false;
    this.captchaResponse = undefined;
    this.supportForm.controls['captchaResponseControl'].reset();
    this.captchaComponent?.resetCaptcha();
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  isInvalid(controlName: string): boolean {
    const control = this.supportForm.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.length ? input.files[0] : undefined;
    // iOS WKWebView: blur after file pick so the native control doesn't keep
    // eating taps meant for Submit.
    input.blur();
  }

  private canSubmit(): boolean {
    const { name, email, message, captchaResponseControl } = this.supportForm.getRawValue();

    if (!name?.trim() || !email?.trim() || !message?.trim() || !this.isValidEmail(email)) {
      return false;
    }

    if (this.isEmbedded) {
      return true;
    }

    return !!captchaResponseControl?.trim() && this.supportForm.controls['captchaResponseControl'].valid;
  }

  private clearAttachmentInput(): void {
    this.selectedFile = undefined;
    const input = document.getElementById('attachment') as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }
}
